<template>
  <div
    class="trigger-node"
    :class="[runClass, { 'trigger-node--active': active }]"
    :data-node-id="id"
  >
    <div class="trigger-node__icon">
      <BaseIcon name="zap" size="16" />
    </div>
    <div class="trigger-node__body">
      <span class="trigger-node__kind">Trigger</span>
      <h3>{{ triggerTypes.find((t) => t.value === nodeData.type)?.label || nodeData.type }}</h3>
      <p v-if="compactSummary" class="trigger-node__summary">{{ compactSummary }}</p>
    </div>
    <div class="trigger-node__footer" v-if="canAddNewNode">
      <button class="add-node-btn" type="button" @click="addNewNode">
        <BaseIcon name="plus" size="12" />
      </button>
    </div>
  </div>
</template>

<script>
  import { ref, watch, computed, onMounted, inject } from 'vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import ActionPickerModal from '../modals/ActionPickerModal.vue';
  import { useFormStore } from '@/store/formStore';
  import { useDealStore } from '@/store/dealStore';
  import { useUIStore } from '@/store/uiStore';
  import { TRIGGER_TYPES } from '../constants/triggers';
  import { WORK_ORDER_TRIGGER_TYPES } from '@/constants/automations';
  import { useVueFlow } from '@vue-flow/core';

  export default {
    name: 'TriggerNode',

    props: {
      id: {
        type: String,
        required: true,
      },
      data: {
        type: Object,
        required: true,
      },
      active: {
        type: Boolean,
        default: false,
      },
    },

    emits: ['update', 'add-node'],
    components: { BaseIcon },
    setup(props, { emit }) {
      const nodeData = ref({
        type: props.data.type,
        config: props.data.config || getDefaultConfig(props.data.type),
      });

      const getDefaultConfig = (type) => {
        switch (type) {
          case 'form_submission':
            return { formId: null };
          case 'lead_status_change':
            return { from_status: null, to_status: null };
          case 'deal_status_change':
            return {
              pipelineId: null,
              fromStageId: '',
              toStageId: '',
              statusType: null,
            };
          case 'proposal_accepted':
          case 'proposal_rejected':
            return { proposalType: 'any' };
          case 'proposal_status_change':
            return { fromStatus: 'any', toStatus: 'any', proposalType: 'any' };
          case 'project_status_change':
            return { fromStatus: 'any', toStatus: 'any' };
          case 'project_fully_completed':
            return { fromStatus: 'any', toStatus: 'completed' };
          case 'work_order_completed':
          case 'work_visit_confirmed':
          case 'work_visit_completed':
          case 'project_all_work_orders_completed':
            return { workOrderType: 'any' };
          case 'scheduled':
            return {
              frequency: 'daily',
              schedule_settings: { time: '09:00' },
            };
          default:
            return {};
        }
      };

      const handleTypeChange = () => {
        nodeData.value.config = getDefaultConfig(nodeData.value.type);
      };
      const { getOutgoers } = useVueFlow();
      const formStore = useFormStore();
      const dealStore = useDealStore();
      const forms = computed(() => formStore.forms);
      const uiStore = useUIStore();

      const STATUS_TYPE_LABELS = {
        any: 'Stage move',
        won: 'Won',
        lost: 'Lost',
        reopened: 'Reopened',
      };

      const dealStatusSummary = computed(() => {
        const cfg = nodeData.value.config;
        if (!cfg) return '';

        const parts = [];

        if (cfg.statusType) {
          parts.push(STATUS_TYPE_LABELS[cfg.statusType] || cfg.statusType);
        }

        if (cfg.pipelineId) {
          const pipeline = dealStore.pipelines.find((p) => p.id === cfg.pipelineId);
          if (pipeline) parts.push(`in ${pipeline.name}`);
        }

        return parts.length ? parts.join(' ') : '';
      });

      const PROPOSAL_TYPE_LABELS = {
        definitive: 'definitieve',
        proforma: 'proforma',
        additional: 'aanvullende',
      };

      const proposalTriggerSummary = computed(() => {
        const event =
          nodeData.value.type === 'proposal_rejected' ? 'afgewezen' : 'geaccepteerd';
        const type = nodeData.value.config?.proposalType;
        if (!type || type === 'any') {
          return `Vuurt zodra een offerte wordt ${event}.`;
        }
        const label = PROPOSAL_TYPE_LABELS[type] || type;
        return `Vuurt zodra een ${label} offerte wordt ${event}.`;
      });

      const STATUS_LABELS = {
        any: 'elke',
        draft: 'concept',
        planning: 'planning',
        active: 'actief',
        completed: 'afgerond',
        cancelled: 'geannuleerd',
        sent: 'verzonden',
        viewed: 'bekeken',
        accepted: 'geaccepteerd',
        rejected: 'afgewezen',
        withdrawn: 'ingetrokken',
        superseded: 'vervangen',
      };

      const proposalStatusChangeSummary = computed(() => {
        const cfg = nodeData.value.config || {};
        const from = STATUS_LABELS[cfg.fromStatus] || cfg.fromStatus || 'elke';
        const to = STATUS_LABELS[cfg.toStatus] || cfg.toStatus || 'elke';
        const type = cfg.proposalType;
        const typeLabel =
          !type || type === 'any' ? '' : ` (${PROPOSAL_TYPE_LABELS[type] || type})`;
        return `Van ${from} → ${to}${typeLabel}`;
      });

      const projectStatusSummary = computed(() => {
        if (nodeData.value.type === 'project_fully_completed') {
          return 'Vuurt wanneer een project volledig is afgerond.';
        }
        const cfg = nodeData.value.config || {};
        const from = STATUS_LABELS[cfg.fromStatus] || cfg.fromStatus || 'elke';
        const to = STATUS_LABELS[cfg.toStatus] || cfg.toStatus || 'elke';
        return `Van ${from} → ${to}`;
      });

      const WORK_ORDER_TYPE_LABELS = {
        any: 'alle types',
        installation: 'installatie',
        measurement: 'inmeten',
        service: 'service',
      };

      const WORK_ORDER_TRIGGER_SUMMARIES = {
        work_order_completed: 'Vuurt bij afronden van een werkbon',
        work_visit_confirmed: 'Vuurt wanneer een bezoekcluster is bevestigd',
        work_visit_completed: 'Vuurt als het laatste bezoek van deze periode klaar is',
        project_all_work_orders_completed: 'Vuurt als alle werkbonnen op het project klaar zijn',
      };

      const workOrderTriggerSummary = computed(() => {
        const type = nodeData.value.config?.workOrderType;
        const label = WORK_ORDER_TYPE_LABELS[type] || type || 'alle types';
        const base =
          WORK_ORDER_TRIGGER_SUMMARIES[nodeData.value.type] ||
          'Vuurt bij afronden';
        return `${base} (${label}).`;
      });

      onMounted(async () => {
        if (!dealStore.pipelines.length) {
          await dealStore.fetchPipelines();
        }
      });

      // Add computed property to check for connections
      const hasConnections = computed(() => {
        return getOutgoers({ id: props.id }).length > 0;
      });

      const addNewNode = () => {
        const modalId = uiStore.addModal(ActionPickerModal, {
          title: 'Add Step',
          description: 'Add a new step to the automation',
          width: 'lg',
          triggerType: nodeData.value.type,
          onSubmit: (action) => {
            emit('add-node', { afterId: props.id, action });
            uiStore.closeModal(modalId);
          },
        });
      };

      const canAddNewNode = computed(() => {
        return (
          !hasConnections.value &&
          !(nodeData.value.type === 'form_submission' && forms.value.length === 0)
        );
      });

      const compactSummary = computed(() => {
        switch (nodeData.value.type) {
          case 'form_submission': {
            const formName = forms.value.find((f) => f.id === nodeData.value.config?.formId)?.name;
            return formName || '';
          }
          case 'deal_status_change':
            return dealStatusSummary.value || 'Elke deal statuswijziging';
          case 'proposal_accepted':
          case 'proposal_rejected':
            return proposalTriggerSummary.value;
          case 'proposal_status_change':
            return proposalStatusChangeSummary.value;
          case 'project_status_change':
          case 'project_fully_completed':
            return projectStatusSummary.value;
          default:
            if (WORK_ORDER_TRIGGER_TYPES.includes(nodeData.value.type)) {
              return workOrderTriggerSummary.value;
            }
            return '';
        }
      });

      const injectedRunStatus = inject(
        'automationRunStatus',
        computed(() => null)
      );
      const runClass = computed(() => {
        const status = injectedRunStatus?.value;
        return status ? `trigger-node--run-${status}` : '';
      });

      // Computed properties for options
      const timeOptions = computed(() => {
        return Array.from({ length: 48 }, (_, i) => {
          const hour = Math.floor(i / 2)
            .toString()
            .padStart(2, '0');
          const minute = i % 2 ? '30' : '00';
          const time = `${hour}:${minute}`;
          return { value: time, label: time };
        });
      });

      const weekDayOptions = computed(() => [
        { value: 1, label: 'Monday' },
        { value: 2, label: 'Tuesday' },
        { value: 3, label: 'Wednesday' },
        { value: 4, label: 'Thursday' },
        { value: 5, label: 'Friday' },
        { value: 6, label: 'Saturday' },
        { value: 0, label: 'Sunday' },
      ]);

      const monthDayOptions = computed(() =>
        Array.from({ length: 31 }, (_, i) => ({
          value: i + 1,
          label: `${i + 1}${['st', 'nd', 'rd'][i] || 'th'}`,
        }))
      );

      const formOptions = computed(() =>
        forms.value.map((form) => ({
          value: form.id,
          label: form.name,
        }))
      );

      watch(
        () => props.data,
        (newData) => {
          nodeData.value = {
            type: newData.type,
            config: newData.config || getDefaultConfig(newData.type),
          };
        },
        { deep: true }
      );

      watch(
        nodeData,
        (newVal) => {
          // emit('update', { id: props.id, data: newVal });
        },
        { deep: true }
      );

      return {
        nodeData,
        triggerTypes: TRIGGER_TYPES,
        compactSummary,
        runClass,
        addNewNode,
        canAddNewNode,
      };
    },
  };
</script>

<style lang="scss" scoped>
  @import '@/assets/scss/base/mixins';

  .trigger-node {
    position: relative;
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    width: 240px;
    padding: 0.875rem 1rem;
    background: #fff;
    border: 1px solid var(--slate-200);
    border-radius: 16px;
    color: var(--slate-900);
    outline: 0 solid transparent;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &--active {
      border-color: var(--slate-900);
      box-shadow: 0 0 0 4px var(--slate-100);
    }

    &--run-completed {
      border-color: var(--green-400);
    }

    &--run-failed {
      border-color: var(--red-400);
    }

    &--run-in_progress,
    &--run-pending {
      border-color: var(--amber-400);
    }

    &__icon {
      width: 32px;
      height: 32px;
      border-radius: 10px;
      background: var(--slate-100);
      color: var(--slate-700);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    &__body {
      min-width: 0;
      flex: 1;

      h3 {
        margin: 0;
        font-size: 0.9375rem;
        font-weight: 600;
        line-height: 1.3;
      }
    }

    &__kind {
      display: block;
      font-size: 0.6875rem;
      font-weight: 500;
      letter-spacing: 0.02em;
      text-transform: uppercase;
      color: var(--slate-500);
      margin-bottom: 0.125rem;
    }

    &__summary {
      margin: 0.25rem 0 0;
      font-size: 0.75rem;
      color: var(--slate-500);
      @include ellipsis(2);
    }

    &__footer {
      display: flex;
      justify-content: center;
      position: absolute;
      bottom: -12px;
      left: 0;
      right: 0;

      .add-node-btn {
        width: 24px;
        height: 24px;
        background: #fff;
        border-radius: 50%;
        border: 1px solid var(--slate-200);
        display: flex;
        align-items: center;
        justify-content: center;

        @include hover {
          background: var(--slate-50);
          border-color: var(--slate-900);
        }
      }
    }
  }
</style>
